import { Injectable } from '@angular/core';
import {
    ClockService,
    DiscountsAndMultipliersService,
    OfferValidity,
    PriceVersion,
    Product,
    Photo,
    SalesDocument,
    SalesDocumentPosition,
    TaxRatesService,
    StaticPositionDetailsService,
    replaceMainPhoto,
    replacePhotos,
    IccDocumentPosition,
} from '@icc/b2b/shared';
import { TranslateService } from '@ngx-translate/core';
import { core } from '@icc/shared';
import { ExchangeRateService, Currency } from '@icc/shared/currency';
import { Configuration } from '@icc/configurator/shared';
import { getUnitInfo, Units } from '@icc/shared/helpers/b2b';
import { PriceBaseService2 } from 'libs/price2/src/lib/price-base.service';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class PositionService {
    constructor(
        private exchangeRateService: ExchangeRateService,
        private taxRateService: TaxRatesService,
        private discountsAndMultipliersService: DiscountsAndMultipliersService,
        private translateService: TranslateService,
        private staticPositionDetailsService: StaticPositionDetailsService,
        private priceBaseService: PriceBaseService2,
        private http: HttpClient
    ) {}

    async createPositionFromProduct(
        salesDocument: SalesDocument,
        documentCurrency: Currency['code'],
        product: Product,
        {
            price,
            quantity = 1,
            unit,
        }: {
            price: { value: number; currency: string; isGross: boolean; taxRate?: number; resetTaxRate?: boolean; unitId: number; unitCode: string };
            quantity?: number;
            unit?: {
                id: number;
                symbol: string;
                code: string;
            };
        },
        documentPriceVersion: PriceVersion,
        currentOfferValidity: OfferValidity,
        positions: SalesDocumentPosition[],
        master?: {
            positionId: string;
            fields: {
                [fieldId: string]: number;
            };
        }
    ): Promise<SalesDocumentPosition> {
        const productTaxRate = this.taxRateService.matchTaxRate(price.taxRate);
        const targetTaxRate =
            salesDocument.isOneTaxRateMode || price.resetTaxRate
                ? this.taxRateService.matchTaxRate(salesDocument.defaultTaxRateId)
                : productTaxRate;
        const department = this.getDepartmentForPosition(positions, product?.departments ?? {});
        const priceCalculationDate = new Date();
        const salesPriceValue = this.discountsAndMultipliersService.getProductSalesPrice(
            price.value,
            product.id
        );

        const position: SalesDocumentPosition = {
            uuid: core.generateUUID(),
            documentId: salesDocument.uuid,
            quantity,
            type: product.type === 'addon' ? 'additional' : 'static',
            configuratorId: null,
            productId: product.id,
            created: new Date(),
            modified: new Date(),
            priceCalculationDate,
            validityDate: this.getPositionValidity(
                priceCalculationDate,
                salesDocument.isConfirmed,
                documentPriceVersion,
                currentOfferValidity
            ),
            machineId: ClockService.getClock().timestamp.node(),
            photos: this.getProductPhotos(product),
            priceMarginPercent: 0,
            priceUnitId: price.unitId,
            priceUnitCode: price.unitCode,
            priceCurrency: documentCurrency,
            priceTaxRateId: targetTaxRate?.id,
            priceTaxRate: targetTaxRate?.value,
            priceIsGross: TaxRatesService.isGross(salesDocument.taxRateCalculationMethod),
            isInvalid: false,
            priceBeforePromotionsValue: TaxRatesService.recalcPrice(
                TaxRatesService.isGross(salesDocument.taxRateCalculationMethod),
                this.exchangeRateService.exchange(
                    salesPriceValue,
                    price.currency,
                    documentCurrency
                ),
                price.isGross,
                price.taxRate,
                targetTaxRate?.value
            ),
            pricePromotions: [],
            priceBeforeDiscountsValue: TaxRatesService.recalcPrice(
                TaxRatesService.isGross(salesDocument.taxRateCalculationMethod),
                this.exchangeRateService.exchange(
                    salesPriceValue,
                    price.currency,
                    documentCurrency
                ),
                price.isGross,
                price.taxRate,
                targetTaxRate?.value
            ),
            priceDiscounts: [],
            priceAfterDiscountsValue: TaxRatesService.recalcPrice(
                TaxRatesService.isGross(salesDocument.taxRateCalculationMethod),
                this.exchangeRateService.exchange(
                    salesPriceValue,
                    price.currency,
                    documentCurrency
                ),
                price.isGross,
                price.taxRate,
                targetTaxRate?.value
            ),
            baseBuyPriceValue: price.value,
            baseBuyPriceCurrency: price.currency,
            baseBuyPriceIsGross: price.isGross ?? false,
            baseBuyPriceTaxRate: price.taxRate ?? null,
            baseBuyPriceTaxRateId: productTaxRate?.id ?? null,
            basePriceCurrency: price.currency,
            basePriceValue: salesPriceValue,
            basePriceIsGross: price.isGross ?? false,
            basePriceTaxRate: price.taxRate ?? null,
            basePriceTaxRateId: productTaxRate?.id ?? null,
            department,
            title: [],
            name: '',
            configuration: {
                productId: product.id,
                extId: product?.extId ? product.extId : null,
                productName: product.name,
                unit,
                code: product?.code,
                index: product?.index ?? null,
                subjectToAdditionalDiscount: product?.subjectToAdditionalDiscount,
            },
            masters: {},
        };
        position.title = this.staticPositionDetailsService.getPositionTitle(
            position,
            this.translateService.instant.bind(this.translateService)
        );

        if (master) {
            position.masters[master.positionId] = master.fields;
        }

        await this.discountsAndMultipliersService.calculateDiscountsAndMultipliers(
            salesDocument,
            position,
            'sales'
        );
        return position;
    }

    async getProduct(id: number): Promise<Product> {
        const product = await firstValueFrom(
            this.http.get<Product>(`${window.location.origin}/api/b2b/product/b2b/${id}`)
        );
        product.photos = replacePhotos(product.photos);
        product.mainPhoto = replaceMainPhoto(product.mainPhoto,product.photos);
        return product;
    }

    async createPositionFromConfiguration(
        salesDocument: SalesDocument,
        documentCurrency: Currency['code'],
        configuration: Configuration,
        title: string[],
        documentPriceVersion: PriceVersion,
        currentOfferValidity: OfferValidity,
        positions: SalesDocumentPosition[],
        copiedPosition?: SalesDocumentPosition,
    ): Promise<SalesDocumentPosition> {
        configuration.code = configuration?.productExtId || configuration?.extId;
        configuration.unit = this.setPCSUnit();
        const baseCurrency = this.exchangeRateService.getBuyCurrency().code;
        const defaultTaxRate = this.taxRateService.getDefaultTaxRate();
        const defaultBuyTaxRate = this.taxRateService.getBuyDefaultTaxRate();
        const taxRate = salesDocument.isOneTaxRateMode
            ? this.taxRateService.matchTaxRate(salesDocument.defaultTaxRateId)
            : defaultTaxRate;
        const configurationTaxRate = this.taxRateService.matchTaxRate(configuration.priceTaxRate);
        const priceCalculationDate = new Date();
        const department = this.getDepartmentForPosition(
            positions,
            configuration?.departments ?? {}
        );

        const name = configuration?.title || '';

        const position: SalesDocumentPosition = {
            uuid: core.generateUUID(),
            documentId: salesDocument.uuid,
            quantity: configuration.quantity || 1,
            type: configuration.type ?? 'complex',
            configuratorId: configuration.configuratorId,
            productId: configuration.productId,
            created: new Date(),
            priceUnitId: 0,
            priceUnitCode: Units.PCS,
            modified: new Date(),
            priceCalculationDate,
            validityDate: this.getPositionValidity(
                priceCalculationDate,
                salesDocument.isConfirmed,
                documentPriceVersion,
                currentOfferValidity
            ),
            machineId: ClockService.getClock().timestamp.node(),
            priceMarginPercent: 0,
            priceCurrency: documentCurrency,
            priceTaxRate: Number(taxRate.value),
            isInvalid: false,
            priceTaxRateId: taxRate.id,
            priceIsGross: TaxRatesService.isGross(salesDocument.taxRateCalculationMethod),
            priceBeforePromotionsValue: TaxRatesService.recalcPrice(
                TaxRatesService.isGross(salesDocument.taxRateCalculationMethod),
                this.exchangeRateService.exchange(
                    configuration.priceAfterMultiplier,
                    baseCurrency,
                    documentCurrency
                ),
                TaxRatesService.isGross(this.taxRateService.getTaxRateCalculationMethod()),
                defaultTaxRate.value,
                taxRate?.value
            ),
            pricePromotions: copiedPosition?.pricePromotions ? core.copy(copiedPosition?.pricePromotions) : [],
            priceBeforeDiscountsValue: TaxRatesService.recalcPrice(
                TaxRatesService.isGross(salesDocument.taxRateCalculationMethod),
                this.exchangeRateService.exchange(
                    configuration.price,
                    baseCurrency,
                    documentCurrency
                ),
                TaxRatesService.isGross(this.taxRateService.getTaxRateCalculationMethod()),
                defaultTaxRate.value,
                taxRate?.value
            ),
            priceDiscounts: copiedPosition?.priceDiscounts ? core.copy(copiedPosition?.priceDiscounts) : [],
            priceAfterDiscountsValue: TaxRatesService.recalcPrice(
                TaxRatesService.isGross(salesDocument.taxRateCalculationMethod),
                this.exchangeRateService.exchange(
                    configuration.price,
                    baseCurrency,
                    documentCurrency
                ),
                TaxRatesService.isGross(this.taxRateService.getTaxRateCalculationMethod()),
                defaultTaxRate.value,
                taxRate?.value
            ),
            baseBuyPriceCurrency: baseCurrency,
            baseBuyPriceValue: TaxRatesService.recalcPrice(
                configuration.priceIsGross ??
                    TaxRatesService.isGross(this.taxRateService.getTaxRateCalculationMethod()),
                configuration.priceAfterMultiplierNoMargin,
                configuration.priceIsGross ??
                    TaxRatesService.isGross(this.taxRateService.getTaxRateCalculationMethod()),
                configuration.priceTaxRate ?? defaultTaxRate.value,
                defaultBuyTaxRate.value
            ),
            baseBuyPriceIsGross:
                configuration.priceIsGross ??
                TaxRatesService.isGross(this.taxRateService.getTaxRateCalculationMethod()),
            baseBuyPriceTaxRate: defaultBuyTaxRate.value,
            baseBuyPriceTaxRateId: defaultBuyTaxRate.id,
            basePriceCurrency: baseCurrency,
            basePriceValue: configuration.priceAfterMultiplier,
            basePriceIsGross:
                configuration.priceIsGross ??
                TaxRatesService.isGross(this.taxRateService.getTaxRateCalculationMethod()),
            basePriceTaxRate: configuration.priceTaxRate ?? defaultTaxRate.value,
            basePriceTaxRateId: configurationTaxRate?.id ?? defaultTaxRate.id,
            configuration: core.copy(configuration),
            name,
            department,
            title,
        };
        await this.discountsAndMultipliersService.calculateDiscountsAndMultipliers(
            salesDocument,
            position,
            'sales'
        );
        return position;
    }

    async updatePositionFromConfiguration(
        activeDocument: SalesDocument,
        position: SalesDocumentPosition,
        configuration: Configuration,
        title: string[]
    ) {
        position.quantity = configuration.quantity || position.quantity;
        const name = configuration?.title || '';
        position.name = name || position.name;
        configuration.code = configuration?.productExtId || configuration?.extId;

        configuration.unit = this.setPCSUnit();

        const configurationTaxRate = this.taxRateService.matchTaxRate(configuration.priceTaxRate);
        const defaultBuyTaxRate = this.taxRateService.getBuyDefaultTaxRate();

        position.baseBuyPriceValue = TaxRatesService.recalcPrice(
            configuration.priceIsGross ??
                TaxRatesService.isGross(this.taxRateService.getTaxRateCalculationMethod()),
            configuration.priceAfterMultiplierNoMargin,
            configuration.priceIsGross ??
                TaxRatesService.isGross(this.taxRateService.getTaxRateCalculationMethod()),
            configuration.priceTaxRate,
            defaultBuyTaxRate?.value ?? configuration.priceTaxRate
        );
        position.baseBuyPriceTaxRate = defaultBuyTaxRate?.value ?? configuration.priceTaxRate;
        position.baseBuyPriceTaxRateId = defaultBuyTaxRate?.id ?? configurationTaxRate?.id;
        position.baseBuyPriceIsGross = configuration.priceIsGross;
        position.basePriceValue = configuration.priceAfterMultiplier;
        position.basePriceTaxRate = configuration.priceTaxRate;
        position.basePriceTaxRateId = configurationTaxRate?.id;
        position.basePriceIsGross = configuration.priceIsGross;
        if (!activeDocument.isCustomTaxRatesMode) {
            position.priceTaxRateId = position.basePriceTaxRateId;
            position.priceTaxRate = position.basePriceTaxRate;
        }
        position.title = title;
        position.configuration = core.copy(configuration);

        await this.discountsAndMultipliersService.calculateDiscountsAndMultipliers(
            activeDocument,
            position,
            'sales'
        );
        return position;
    }

    async createBuyPositionFromSalesPosition(
        salesPosition: SalesDocumentPosition,
        buyDocument: SalesDocument,
        documentPriceVersion: PriceVersion,
        currentOfferValidity: OfferValidity,
        buyPositionsMap: Map<string, SalesDocumentPosition>
    ): Promise<SalesDocumentPosition> {
        const priceCalculationDate = new Date();
        const targetTaxRate = buyDocument.isOneTaxRateMode
            ? buyDocument.defaultTaxRate
            : salesPosition.baseBuyPriceTaxRate;
        const targetTaxRateId = buyDocument.isOneTaxRateMode
            ? buyDocument.defaultTaxRateId
            : salesPosition.baseBuyPriceTaxRateId;

        const configuration = core.copy(salesPosition.configuration);
        if (configuration.priceSegmentsNoMargin) {
            configuration.priceSegments = configuration.priceSegmentsNoMargin;
        }
        if (configuration.pricePartsNoMargin) {
            configuration.priceParts = configuration.pricePartsNoMargin;
        }
        if (configuration.discountGroupsNoMargin) {
            configuration.discountGroups = configuration.discountGroupsNoMargin;
        }
        if (configuration.priceAfterMultiplierNoMargin) {
            configuration.priceAfterMultiplier = configuration.priceAfterMultiplierNoMargin;
        }
        if (configuration.priceNoMargin) {
            configuration.price = configuration.priceNoMargin;
        }
        if (configuration.PriceSegmentsNoMargin) {
            configuration.PriceSegments = configuration.PriceSegmentsNoMargin;
        }
        if (configuration.PricePartsNoMargin) {
            configuration.PriceParts = configuration.PricePartsNoMargin;
        }
        if (configuration.DiscountGroupsNoMargin) {
            configuration.DiscountGroups = configuration.DiscountGroupsNoMargin;
        }
        if (configuration.PriceNoMargin) {
            configuration.Price = configuration.PriceNoMargin;
        }
        const position: SalesDocumentPosition = {
            ...salesPosition,
            uuid: core.generateUUID(),
            documentId: buyDocument.uuid,
            baseBuyPriceCurrency: null,
            baseBuyPriceValue: null,
            baseBuyPriceIsGross: null,
            baseBuyPriceTaxRate: null,
            baseBuyPriceTaxRateId: null,
            basePriceCurrency: salesPosition.baseBuyPriceCurrency,
            basePriceValue: salesPosition.baseBuyPriceValue,
            basePriceIsGross: salesPosition.baseBuyPriceIsGross,
            basePriceTaxRate: salesPosition.baseBuyPriceTaxRate,
            basePriceTaxRateId: salesPosition.baseBuyPriceTaxRateId,
            priceTaxRate: targetTaxRate,
            priceTaxRateId: targetTaxRateId,
            priceBeforePromotionsValue: TaxRatesService.recalcPrice(
                TaxRatesService.isGross(buyDocument.taxRateCalculationMethod),
                this.exchangeRateService.exchange(
                    salesPosition.baseBuyPriceValue,
                    salesPosition.baseBuyPriceCurrency,
                    buyDocument.priceCurrency
                ),
                salesPosition.baseBuyPriceIsGross,
                salesPosition.baseBuyPriceTaxRate,
                targetTaxRate
            ),
            pricePromotions: [],
            priceBeforeDiscountsValue: TaxRatesService.recalcPrice(
                TaxRatesService.isGross(buyDocument.taxRateCalculationMethod),
                this.exchangeRateService.exchange(
                    salesPosition.baseBuyPriceValue,
                    salesPosition.baseBuyPriceCurrency,
                    buyDocument.priceCurrency
                ),
                salesPosition.baseBuyPriceIsGross,
                salesPosition.baseBuyPriceTaxRate,
                targetTaxRate
            ),
            priceCurrency: buyDocument.priceCurrency,
            priceAfterDiscountsValue: TaxRatesService.recalcPrice(
                TaxRatesService.isGross(buyDocument.taxRateCalculationMethod),
                this.exchangeRateService.exchange(
                    salesPosition.baseBuyPriceValue,
                    salesPosition.baseBuyPriceCurrency,
                    buyDocument.priceCurrency
                ),
                salesPosition.baseBuyPriceIsGross,
                salesPosition.baseBuyPriceTaxRate,
                targetTaxRate
            ),
            priceIsGross: TaxRatesService.isGross(buyDocument.taxRateCalculationMethod),
            priceDiscounts: [],
            configuration,
            created: new Date(),
            modified: new Date(),
            priceCalculationDate: salesPosition.customPriceValue
                ? salesPosition.priceCalculationDate
                : priceCalculationDate,
            validityDate: salesPosition.customPriceValue
                ? salesPosition.validityDate
                : this.getPositionValidity(
                      priceCalculationDate,
                      buyDocument.isConfirmed,
                      documentPriceVersion,
                      currentOfferValidity
                  ),
            machineId: ClockService.getClock().timestamp.node(),
            masters: {},
        };

        this.createBuyMasters(salesPosition, buyPositionsMap, position);

        await this.discountsAndMultipliersService.calculateDiscountsAndMultipliers(
            buyDocument,
            position,
            'buy'
        );
        return position;
    }

    private createBuyMasters(
        salesPosition: SalesDocumentPosition,
        buyPositionsMap: Map<string, SalesDocumentPosition>,
        position: SalesDocumentPosition
    ) {
        if (salesPosition.masters) {
            for (const masterId of Object.keys(salesPosition.masters)) {
                const master = buyPositionsMap.get(masterId);
                if (master && master.documentId === position.documentId) {
                    position.masters[master.uuid] = salesPosition.masters[masterId];
                }
            }
        }
    }

    async changesForBuyPositionFromSalesPosition(
        buyDocument: SalesDocument,
        position: SalesDocumentPosition,
        changes: Partial<SalesDocumentPosition>,
        documentPriceVersion: PriceVersion,
        currentOfferValidity: OfferValidity,
        buyPositionsMap: Map<string, SalesDocumentPosition>,
    ) {
        if (
            'baseBuyPriceValue' in changes ||
            (changes.quantity && buyDocument.discountType === 'value') ||
            'configuration' in changes ||
            'slaves' in changes ||
            'masters' in changes
        ) {
            const priceCalculationDate = new Date();
            (
                [
                    'baseBuyPriceValue',
                    'baseBuyPriceCurrency',
                    'baseBuyPriceIsGross',
                    'baseBuyPriceTaxRate',
                    'baseBuyPriceTaxRateId',
                ] as const
            ).forEach((key) => {
                if (key in changes) {
                    position[key.replace('Buy', '')] = changes[key];
                    changes[key.replace('Buy', '')] = position[key.replace('Buy', '')];
                    (changes as Record<typeof key, typeof changes[typeof key]>)[key] = null;
                    if (!buyDocument.isCustomTaxRatesMode && key === 'baseBuyPriceTaxRate') {
                        position.priceTaxRate = position.basePriceTaxRate;
                    }
                    if (!buyDocument.isCustomTaxRatesMode && key === 'baseBuyPriceTaxRateId') {
                        position.priceTaxRateId = position.basePriceTaxRateId;
                    }
                }
            });
            if ('quantity' in changes) {
                position.quantity = changes.quantity;
            }
            if ('masters' in changes) {
                const masters = core.copy(changes.masters);
                position.masters = {};
                for (const positionId of Object.keys(masters)) {
                    if (buyPositionsMap.get(positionId)?.documentId === position.documentId) {
                        position.masters[buyPositionsMap.get(positionId).uuid] = masters[positionId];
                    }
                }
                changes.masters = position.masters;
            }

            let configuration = core.copy(position.configuration);
            if ('configuration' in changes) {
                configuration = core.copy(core.parseJson(changes.configuration));

                if (configuration.priceSegmentsNoMargin) {
                    configuration.priceSegments = configuration.priceSegmentsNoMargin;
                }
                if (configuration.pricePartsNoMargin) {
                    configuration.priceParts = configuration.pricePartsNoMargin;
                }
                if (configuration.discountGroupsNoMargin) {
                    configuration.discountGroups = configuration.discountGroupsNoMargin;
                }
                if (configuration.priceAfterMultiplierNoMargin) {
                    configuration.priceAfterMultiplier = configuration.priceAfterMultiplierNoMargin;
                }
                if (configuration.priceNoMargin) {
                    configuration.price = configuration.priceNoMargin;
                }
                if (configuration.PriceSegmentsNoMargin) {
                    configuration.PriceSegments = configuration.PriceSegmentsNoMargin;
                }
                if (configuration.PricePartsNoMargin) {
                    configuration.PriceParts = configuration.PricePartsNoMargin;
                }
                if (configuration.DiscountGroupsNoMargin) {
                    configuration.DiscountGroups = configuration.DiscountGroupsNoMargin;
                }
                if (configuration.PriceNoMargin) {
                    configuration.Price = configuration.PriceNoMargin;
                }
            }
            position.configuration = configuration;

            await this.discountsAndMultipliersService.calculateDiscountsAndMultipliers(
                buyDocument,
                position,
                'buy'
            );
            changes.priceBeforePromotionsValue = position.priceBeforePromotionsValue;
            changes.pricePromotions = position.pricePromotions;
            changes.priceBeforeDiscountsValue = position.priceBeforeDiscountsValue;
            changes.priceAfterDiscountsValue = position.priceAfterDiscountsValue;
            changes.priceDiscounts = position.priceDiscounts;
            changes.priceCurrency = position.priceCurrency;
            changes.priceCalculationDate = priceCalculationDate;
            changes.configuration = position.configuration;
            if (!buyDocument.isCustomTaxRatesMode) {
                changes.priceTaxRate = position.priceTaxRate;
                changes.priceTaxRateId = position.priceTaxRateId;
            }
            changes.priceIsGross = position.priceIsGross;
            changes.validityDate = this.getPositionValidity(
                priceCalculationDate,
                buyDocument.isConfirmed,
                documentPriceVersion,
                currentOfferValidity
            );
            if ('slaves' in changes) {
                delete changes.slaves;
            }
        }
        return changes;
    }

    updateSlaves(slaves: IccDocumentPosition['slaves'], position: IccDocumentPosition, buyPositionsMap: Map<string, SalesDocumentPosition>) {
        slaves = core.copy(slaves);
        const newSlaves = {};
        for (const fieldId of Object.keys(slaves)) {
            if (buyPositionsMap.get(slaves[fieldId].positionId)?.documentId === position.documentId) {
                newSlaves[fieldId] = {
                    positionId: buyPositionsMap.get(slaves[fieldId].positionId).uuid,
                    productId: slaves[fieldId].productId,
                    unitId: slaves[fieldId].unitId,
                    unitCode: slaves[fieldId].unitCode,
                };
            }
        }
        return newSlaves;
    }

    async createSalesPositionFromBuyPosition(
        buyPosition: SalesDocumentPosition,
        salesDocument: SalesDocument,
        documentPriceVersion: PriceVersion,
        currentOfferValidity: OfferValidity
    ): Promise<SalesDocumentPosition> {
        const priceCalculationDate = new Date();
        const targetTaxRate = salesDocument.isOneTaxRateMode
            ? salesDocument.defaultTaxRate
            : buyPosition.basePriceTaxRate;
        const targetTaxRateId = salesDocument.isOneTaxRateMode
            ? salesDocument.defaultTaxRateId
            : buyPosition.basePriceTaxRateId;
        const salesBasePriceValue = this.discountsAndMultipliersService.getPositionSalesPrice(
            buyPosition.basePriceValue,
            buyPosition
        );
        const priceSettings = {
            taxRate: targetTaxRate,
            isGross: salesDocument.priceIsGross,
            charge: this.discountsAndMultipliersService.charge,
            multiplier: this.discountsAndMultipliersService.getPositionMultiplier(buyPosition),
            marketFactor: this.discountsAndMultipliersService.marketFactor,
            priceVersionId: documentPriceVersion.id,
            contractorCompanyId: salesDocument.sellerCompanyId,
            promotion: buyPosition.configuration.configuratorId ?
                this.discountsAndMultipliersService.getConfiguratorPromotion(buyPosition.configuration.configuratorId) :
                null,
        };
        const configuration = core.copy(buyPosition.configuration);
        if (configuration.priceSegments) {
            this.priceBaseService.setPreCalculatedPrice(configuration, priceSettings);
            configuration.price = configuration.Price;
            configuration.priceNoMargin = configuration.PriceNoMargin || configuration.Price;
            configuration.priceAfterMultiplier =
                configuration.PriceAfterMultiplier || configuration.Price;
            configuration.priceAfterMultiplierNoMargin =
                configuration.PriceAfterMultiplierNoMargin || configuration.PriceNoMargin;
            configuration.priceAfterDiscounts =
                configuration.PriceAfterDiscounts || configuration.Price;
            configuration.priceIsGross = configuration.PriceIsGross || false;
            configuration.priceTaxRate = configuration.PriceTaxRate || 0;
            if (configuration.Quantity) {
                configuration.quantity = configuration.Quantity;
            }
            configuration.priceSegments = configuration.PriceSegments;
            configuration.priceSegmentsNoMargin = configuration.PriceSegmentsNoMargin;
            configuration.priceParts = configuration.PriceParts;
            configuration.pricePartsNoMargin = configuration.PricePartsNoMargin;
            if (configuration.DiscountGroups) {
                configuration.discountGroups = configuration.DiscountGroups;
            }
            if (configuration.DiscountGroupsNoMargin) {
                configuration.discountGroupsNoMargin = configuration.DiscountGroupsNoMargin;
            }
        }

        const position: SalesDocumentPosition = {
            ...buyPosition,
            uuid: core.generateUUID(),
            documentId: salesDocument.uuid,
            baseBuyPriceCurrency: buyPosition.basePriceCurrency,
            baseBuyPriceValue: buyPosition.basePriceValue,
            baseBuyPriceIsGross: buyPosition.basePriceIsGross,
            baseBuyPriceTaxRate: buyPosition.basePriceTaxRate,
            baseBuyPriceTaxRateId: buyPosition.basePriceTaxRateId,
            basePriceCurrency: buyPosition.basePriceCurrency,
            basePriceValue: salesBasePriceValue,
            basePriceIsGross: buyPosition.basePriceIsGross,
            basePriceTaxRate: buyPosition.basePriceTaxRate,
            basePriceTaxRateId: buyPosition.basePriceTaxRateId,
            priceTaxRate: targetTaxRate,
            priceTaxRateId: targetTaxRateId,
            priceBeforePromotionsValue: TaxRatesService.recalcPrice(
                TaxRatesService.isGross(salesDocument.taxRateCalculationMethod),
                this.exchangeRateService.exchange(
                    salesBasePriceValue,
                    buyPosition.basePriceCurrency,
                    salesDocument.priceCurrency
                ),
                buyPosition.basePriceIsGross,
                buyPosition.basePriceTaxRate,
                targetTaxRate
            ),
            pricePromotions: [],
            priceBeforeDiscountsValue: TaxRatesService.recalcPrice(
                TaxRatesService.isGross(salesDocument.taxRateCalculationMethod),
                this.exchangeRateService.exchange(
                    salesBasePriceValue,
                    buyPosition.basePriceCurrency,
                    salesDocument.priceCurrency
                ),
                buyPosition.basePriceIsGross,
                buyPosition.basePriceTaxRate,
                targetTaxRate
            ),
            priceCurrency: salesDocument.priceCurrency,
            priceAfterDiscountsValue: TaxRatesService.recalcPrice(
                TaxRatesService.isGross(salesDocument.taxRateCalculationMethod),
                this.exchangeRateService.exchange(
                    salesBasePriceValue,
                    buyPosition.basePriceCurrency,
                    salesDocument.priceCurrency
                ),
                buyPosition.basePriceIsGross,
                buyPosition.basePriceTaxRate,
                targetTaxRate
            ),
            priceIsGross: TaxRatesService.isGross(salesDocument.taxRateCalculationMethod),
            priceDiscounts: [],
            configuration: configuration,
            created: new Date(),
            modified: new Date(),
            priceCalculationDate: buyPosition.customPriceValue
                ? buyPosition.priceCalculationDate
                : priceCalculationDate,
            validityDate: buyPosition.customPriceValue
                ? buyPosition.validityDate
                : this.getPositionValidity(
                    priceCalculationDate,
                    salesDocument.isConfirmed,
                    documentPriceVersion,
                    currentOfferValidity
                ),
            machineId: ClockService.getClock().timestamp.node(),
            masters: {},
            slaves: {},
        };
        await this.discountsAndMultipliersService.calculateDiscountsAndMultipliers(
            salesDocument,
            position,
            'sales'
        );
        return position;
    }

    public getPositionValidity(
        priceCalculationDate: Date,
        isDocumentConfirmed: boolean,
        documentPriceVersion: PriceVersion,
        currentOfferValidity: OfferValidity
    ) {
        const offerValidity = new Date(priceCalculationDate.getTime());
        if (currentOfferValidity?.value) {
            offerValidity.setDate(offerValidity.getDate() + Number(currentOfferValidity.value));
        } else if (documentPriceVersion) {
            return null;
        }
        return offerValidity;
    }

    private getDepartmentsIds(positions: SalesDocumentPosition[]): number[] {
        const departmentsIds = [];
        if (positions?.length) {
            for (const position of positions) {
                const positionDepartment = position?.department ? position.department : null;
                if (positionDepartment?.id) {
                    departmentsIds.push(positionDepartment.id);
                }
            }
        }
        return departmentsIds;
    }

    private getDepartmentForPosition(
        positions: SalesDocumentPosition[],
        departments: Record<number, string>
    ): {
        id: number | null;
        name: string;
    } {
        const departmentsIds = this.getDepartmentsIds(positions);
        const ids = Object.keys(departments).map((id) => +id);
        let id: number | null = null;

        if (ids?.length) {
            for (const productDepartmentId of ids) {
                if (departmentsIds.includes(+productDepartmentId)) {
                    id = +productDepartmentId;
                    break;
                }
            }
            if (!id) id = ids[0];
        }
        return { id, name: id && departments[id] ? departments[id] : '' };
    }

    private getProductPhotos(product: Product): Photo[] {
        return product?.mainPhoto?.img
            ? [product.mainPhoto]
            : product?.photos?.length
            ? [product?.photos[0]]
            : [];
    }

    private setPCSUnit() {
        return getUnitInfo(Units.PCS);
    }
}
